import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PromisePool } from 'es6-promise-pool';
import { animateScroll as scroll } from 'react-scroll';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Label,
  Input,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModal } from '../../actions';
import { showConfirmationModal } from '../../actions/App';
import { deleteFile } from '../../actions/Files';

import {
  APIgetDuplicates,
  APIdeleteDuplicate,
  APIgetDuplicate,
  APIhideDuplicate,
  APIunhideDuplicate,
} from '../../api/Files';

import DuplicateListItem from '../../components/Admin/Duplicates/ListItem';
import DuplicatePreview from '../../components/Admin/Duplicates/Preview';

import { sendTrackingEvent } from '../../helpers';
import { formatBytes } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { goalSortSetting } from '../../helpers/Sort';

import useUpdateEffect from '../../hooks/useUpdateEffect';

import Loading from '../../components/Loading';
import LoadingSmall from '../../components/LoadingSmall';
import UpgradePlanButton from '../../components/UpgradePlanButton';
import TrialNotice from '../../components/TrialNotice';

const AdminDuplicates = ({ dispatch }) => {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesTotals, setDuplicatesTotals] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewDuplicate, setPreviewDuplicate] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [mode, setMode] = useState('images');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState('updated_at');
  const [end, setEnd] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const intl = useIntl();

  useEffect(() => {
    if (initialized || !duplicatesTotals) {
      return;
    }

    Object.keys(duplicatesTotals).every((type) => {
      if (duplicatesTotals[type] > 0) {
        setMode(type);
        return false;
      }
      return true;
    });

    setInitialized(true);
  }, [initialized, duplicatesTotals]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!page) {
      setPage(1);
      return;
    }

    setLoading(true);
    APIgetDuplicates({
      page,
      mode,
      order_by: orderBy,
    }).then((response) => {
      const {
        data,
        meta,
        totals,
      } = response.data;

      setDuplicatesTotals(totals);
      setDuplicates(duplicates.concat(data));
      setLoading(false);
      setEnd(meta.current_page >= meta.last_page);
    });
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToTop = () => {
    scroll.scrollToTop({
      containerId: 'duplicates-content',
      duration: 500,
      smooth: true,
    });
  };

  const reloadDuplicates = () => {
    setDuplicates([]);
    setPreviewDuplicate(null);
    setEnd(false);
    setPage(0);
    scrollToTop();
  };

  useUpdateEffect(() => {
    reloadDuplicates();
  }, [mode, orderBy]);

  const updateDuplicateAfterMerge = (duplicate) => {
    if (duplicate.files.length > 1) {
      const duplicateIdx = duplicates.findIndex(d => (d.uuid === duplicate.uuid));
      if (duplicateIdx >= 0) {
        duplicates[duplicateIdx] = duplicate;
        setDuplicates(duplicates);
        setPreviewDuplicate(duplicate);
      }
    } else {
      setDuplicates(duplicates.filter(d => (d.uuid !== duplicate.uuid)));
      setPreviewDuplicate(null);
    }
    scrollToTop();
    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'Files have been merged' }),
    });
  };

  const openFileMergeModal = (e) => {
    e.preventDefault();
    const duplicate = duplicates.find(d => (d.uuid === e.target.id));
    dispatch(showModal('FileMergeModal', {
      className: 'modal-file-merge modal-xl',
      duplicateUuid: e.target.id,
      duplicate,
      selectedFiles: selectedFiles.length === 0 ?
        duplicate.files.map(d => (d.uuid)) : selectedFiles,
      backdrop: false,
      afterFileMerge: updateDuplicateAfterMerge,
    }));
  };

  const getDuplicatesSize = (modeId = null) => (
    (duplicatesTotals && duplicatesTotals[`${modeId || mode}_size`] > 0) ?
      formatBytes(duplicatesTotals[`${modeId || mode}_size`]) : null
  );

  const getModeDescription = (modeId = null) => {
    let description = '';
    const currentMode = modeId || mode;

    switch (currentMode) {
      case 'images':
        description = intl.formatMessage({ defaultMessage: 'Duplicate images' });
        break;
      case 'similar_images':
        description = intl.formatMessage({ defaultMessage: 'Similar images' });
        break;
      case 'files':
      default:
        description = intl.formatMessage({ defaultMessage: 'Duplicate files' });
    }

    if (duplicatesTotals) {
      description = `${description} (${duplicatesTotals[currentMode]})`;
    }

    return description;
  };

  const handleSwitchSelected = (fileUuid) => {
    if (selectedFiles.includes(fileUuid)) {
      setSelectedFiles(selectedFiles.filter(sf => (sf !== fileUuid)));
    } else {
      setSelectedFiles(selectedFiles.concat([fileUuid]));
    }
  };

  const handleUnselectAll = () => {
    setSelectedFiles([]);
  };

  const handleSwitchSelectAll = () => {
    if (selectedFiles.length === previewDuplicate.files.length) {
      handleUnselectAll();
    } else {
      setSelectedFiles(previewDuplicate.files.map(d => (d.uuid)));
    }
  };

  const handleSetDuplicate = (duplicate) => {
    handleUnselectAll();
    scrollToTop();
    setDuplicateLoading(true);
    setPreviewDuplicate(null);
    setError(null);
    APIgetDuplicate(duplicate.uuid).then((response) => {
      const { data } = response;
      setPreviewDuplicate(data);
      setDuplicateLoading(false);
    }).catch((response) => {
      if (response.response.status) {
        setError(response.response.status);
      }
      setDuplicateLoading(false);
      return error;
    });
  };

  const handleSwitchIgnore = (e) => {
    e.preventDefault();

    if (previewDuplicate.hidden) {
      setHiding(true);
      return APIunhideDuplicate(previewDuplicate.uuid).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The duplicate is now unignored' }),
        });
        setDuplicates(duplicates.filter(d => (d.uuid !== previewDuplicate.uuid)));
        duplicatesTotals[mode] -= 1;
        setDuplicatesTotals(duplicatesTotals);
        setPreviewDuplicate(null);
        setHiding(false);

        sendTrackingEvent('duplicate-unignored');
      });
    }

    const text = `${mode === 'similar_images' ? 'similar' : 'duplicate'} ${mode === 'files' ? 'files' : 'images'}`;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Ignore {text}?' }, { text }),
      text: intl.formatMessage({ defaultMessage: 'Ignored sets of {text} will be removed from the list.' }, { text }),
      confirmText: intl.formatMessage({ defaultMessage: 'Ignore' }),
      onConfirm: () => {
        setHiding(true);
        return APIhideDuplicate(previewDuplicate.uuid).then(() => {
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'The set of {text} is now ignored' }, { text }),
          });
          setDuplicates(duplicates.filter(d => (d.uuid !== previewDuplicate.uuid)));
          duplicatesTotals[mode] -= 1;
          setDuplicatesTotals(duplicatesTotals);
          setPreviewDuplicate(null);
          setHiding(false);

          sendTrackingEvent('duplicate-ignored');
        });
      },
    };
    return dispatch(showConfirmationModal(modalParams));
  };

  const removeFilesFromPreviewDuplicate = (files) => {
    const newDuplicate = { ...previewDuplicate };
    newDuplicate.files = newDuplicate.files.filter(f => (!files.includes(f.uuid)));
    setPreviewDuplicate(newDuplicate);
    if (newDuplicate.files.length > 1) {
      const duplicateIdx = duplicates.findIndex(d => (d.uuid === newDuplicate.uuid));
      if (duplicateIdx >= 0) {
        duplicates[duplicateIdx] = newDuplicate;
        setDuplicates(duplicates);
      }
    } else {
      APIdeleteDuplicate(newDuplicate.uuid);
      setDuplicates(duplicates.filter(d => (d.uuid !== newDuplicate.uuid)));
      setPreviewDuplicate(null);
    }
    scrollToTop();
  };

  const deleteFiles = (files) => {
    let counter = 0;
    let errors = 0;
    const promiseProducer = () => {
      if (counter < files.length) {
        const file = files[counter];
        counter += 1;
        const params = {
          hash_type: mode === 'similar_images' ? 'similar' : 'sha1',
        };
        return dispatch(deleteFile(file, params)).then((response) => {
          if (response.response && response.response.status === 403) {
            errors += 1;
          }
        });
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 1);
    return pool.start().then(() => {
      if (errors > 0) {
        if (errors === files.length) {
          addFlashMessage({
            type: 'danger',
            text: intl.formatMessage({ defaultMessage: 'You don\'t have permissions to delete this {errors, plural, one {file} other {files}}' }),
          });
        } else {
          addFlashMessage({
            type: 'warning',
            text: intl.formatMessage({ defaultMessage: 'You don\'t have permissions to delete some of the selected files' }),
          });
        }
      } else {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Files have been deleted' }),
        });

        removeFilesFromPreviewDuplicate(files);

        sendTrackingEvent('duplicate-files-deleted');
      }
    });
  };

  const handleDeleteFiles = (files) => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage(
        { defaultMessage: 'Are you sure you want to delete {filesCount} {filesCount, plural, one {file} other {files}}? If you delete {filesCount, plural, one {this file it} other {these files they}} will be deleted permanently from your workspace and all collections it may be a part of - this can not be undone!' },
        { filesCount: files.length },
      ),
      confirmText: intl.formatMessage({ defaultMessage: 'Delete' }),
      onConfirm: () => (
        deleteFiles(files).then(() => {
          handleUnselectAll();
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  };

  const updateArchivedFiles = (files) => {
    const newDuplicate = { ...previewDuplicate };
    newDuplicate.files = newDuplicate.files.map((f) => {
      const updatedFile = files.find(file => (file.uuid === f.uuid));
      return ({
        ...f,
        archived: updatedFile ? true : f.archived,
        updated_at: updatedFile ? new Date() : f.updated_at,
      });
    });
    handleUnselectAll();
    setPreviewDuplicate(newDuplicate);
    scrollToTop();
  };

  const handleArchiveFiles = (files) => {
    dispatch(showModal('ArchiveModal', {
      className: 'modal-archive modal-file-archive',
      files: previewDuplicate.files.filter(df => (files.includes(df.uuid))),
      action: 'archive',
      afterFilesArchived: updateArchivedFiles,
    }));
  };

  const openBulkMergeModal = () => {
    dispatch(showModal('BulkMergeModal', {
      className: 'modal-bulk-merge',
      mode,
      afterMerge: reloadDuplicates,
    }));
  };

  const goalSortSettingValue = goalSortSetting(orderBy);

  return (
    <div id="duplicates-content" className="main-content">
      <Container fluid>
        <header className="page-header duplicates-header">
          <div className="page-header-left with-upgrade-button">
            <h2 className="page-title">
              <i className="far fa-copy" />
              {intl.formatMessage({ defaultMessage: 'Duplicates' })}
              {(duplicatesTotals && duplicatesTotals.size > 0) && (
                ` ${intl.formatMessage({ defaultMessage: '({size} in total)' }, { size: formatBytes(duplicatesTotals.size) })}`
              )}
            </h2>
            <UpgradePlanButton feature="duplicate-files" />
          </div>
          <div className="header-actions files-actions">
            {previewDuplicate && (
              <>
                <Label htmlFor="FilesSelectAll" id="FilesSelectAllLabel">
                  {previewDuplicate.files.length} file{previewDuplicate.files.length === 1 ? '' : 's'}
                </Label>
                <Input
                  type="checkbox"
                  id="FilesSelectAll"
                  className="input-check check-l"
                  checked={selectedFiles.length === previewDuplicate.files.length}
                  onChange={handleSwitchSelectAll}
                />
                <UncontrolledTooltip placement="bottom" target="FilesSelectAllLabel">
                  {intl.formatMessage({ defaultMessage: '{shouldUnselect, select, yes {Unselect} other {Select}} {filesCount} {filesCount, plural, one {file} other {files}}' }, {
                    shouldUnselect: selectedFiles.length === previewDuplicate.files.length ? 'yes' : 'no',
                    filesCount: previewDuplicate.files.length,
                  })}
                </UncontrolledTooltip>
              </>
            )}
            <UncontrolledDropdown>
              <DropdownToggle
                id="SortDuplicates"
                className="transparent-button"
                disabled={loading}
              >
                <i className="fal fa-arrow-down-arrow-up mr-0" />
              </DropdownToggle>
              <UncontrolledTooltip target="SortDuplicates" fade={false} placement="top" boundariesElement="body">
                {intl.formatMessage({ defaultMessage: 'Sort' })}
                {goalSortSettingValue && (
                  <>
                    {': '}
                    {goalSortSettingValue.label}
                  </>
                )}
              </UncontrolledTooltip>
              <DropdownMenu>
                <DropdownItem
                  active={orderBy === 'updated_at'}
                  onClick={() => {
                    setOrderBy('updated_at');
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'Recently updated' })}
                </DropdownItem>
                <DropdownItem
                  active={orderBy === 'duplicates_size'}
                  onClick={() => {
                    setOrderBy('duplicates_size');
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'Size' })}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <UncontrolledDropdown>
              <DropdownToggle disabled={loading} caret>
                {mode === 'images' && (
                  <>
                    <i className="fas fa-images" />
                    <span>
                      {getModeDescription('images')}
                    </span>
                  </>
                )}
                {mode === 'similar_images' && (
                  <>
                    <i className="fas fa-images" />
                    <span>
                      {getModeDescription('similar_images')}
                    </span>
                  </>
                )}
                {mode === 'files' && (
                  <>
                    <i className="fas fa-files" />
                    <span>
                      {getModeDescription('files')}
                    </span>
                  </>
                )}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem
                  active={mode === 'images'}
                  onClick={() => {
                    setMode('images');
                  }}
                >
                  {getModeDescription('images')}
                </DropdownItem>
                <DropdownItem
                  active={mode === 'similar_images'}
                  onClick={() => {
                    setMode('similar_images');
                  }}
                >
                  {getModeDescription('similar_images')}
                </DropdownItem>
                <DropdownItem
                  active={mode === 'files'}
                  onClick={() => {
                    setMode('files');
                  }}
                >
                  {getModeDescription('files')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <Button
              id="bulk-merge-button"
              color="primary"
              disabled={mode === 'similar_images'}
              onClick={openBulkMergeModal}
            >
              {intl.formatMessage({ defaultMessage: 'Bulk merge' })}
            </Button>
            {mode === 'similar_images' && (
              <UncontrolledTooltip fade={false} target="bulk-merge-button">
                {intl.formatMessage({ defaultMessage: 'You can\'t bulk merge similar images' })}
              </UncontrolledTooltip>
            )}
          </div>
        </header>

        <TrialNotice feature="duplicate-files" />

        <div className="content-wrapper">
          {mode === 'similar_images' ? (
            <p>
              {intl.formatMessage({ defaultMessage: 'Archive or delete similar images as needed. You can\'t merge similar images.' })}
            </p>
          ) : (
            <p>
              {intl.formatMessage(
                { defaultMessage: 'Manage your duplicate {modeFiles, select, yes {files} other {images}} by deleting or merging unnecessary copies.' },
                {
                  modeFiles: mode === 'files' ? 'yes' : 'no',
                },
              )}
              {(duplicatesTotals && getDuplicatesSize()) && (
                <>
                  {' '}
                  {intl.formatMessage({ defaultMessage: 'You can save up to <strong>{size}</strong>' }, {
                    strong: chunks => <strong>{chunks}</strong>,
                    size: getDuplicatesSize(),
                  })}.
                </>
              )}
            </p>
          )}
        </div>
        <div className="duplicates-wrapper">
          <div className="duplicates-sidebar">
            <Card>
              <CardHeader>
                {getModeDescription()}
              </CardHeader>
              <CardBody>
                {(!loading && duplicates.length === 0) && (
                  <p className="center">
                    {intl.formatMessage({ defaultMessage: 'No duplicates found.' })}
                  </p>
                )}
                <ul className="duplicated-files-list" role="menu">
                  {duplicates.map(duplicate => (
                    <DuplicateListItem
                      key={duplicate.uuid}
                      duplicate={duplicate}
                      mode={mode}
                      selected={!!(previewDuplicate && previewDuplicate.uuid === duplicate.uuid)}
                      handleSetDuplicate={() => {
                        handleSetDuplicate(duplicate);
                      }}
                    />
                  ))}
                </ul>
                {loading && (
                  <LoadingSmall />
                )}
                {(!end && !loading) && (
                  <p className="center">
                    <Button
                      size="sm"
                      onClick={() => {
                        setPage(page + 1);
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Load more' })}
                    </Button>
                  </p>
                )}
              </CardBody>
            </Card>
          </div>
          <div className="duplicates-main">
            {error && (
              <p className="center error">
                <strong>
                  {error === 404 ? (
                    intl.formatMessage({ defaultMessage: 'Selected duplicate could not be found. Maybe it has been deleted.' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again or contact us.' })
                  )}
                </strong>
              </p>
            )}
            {(previewDuplicate || duplicateLoading) ? (
              <DuplicatePreview
                duplicate={previewDuplicate}
                loading={duplicateLoading}
                hiding={hiding}
                error={error}
                mode={mode}
                selectedFiles={selectedFiles}
                openFileMergeModal={openFileMergeModal}
                handleSwitchSelected={handleSwitchSelected}
                handleUnselectAll={handleUnselectAll}
                handleArchiveFiles={handleArchiveFiles}
                handleDeleteFiles={handleDeleteFiles}
                handleSwitchIgnore={handleSwitchIgnore}
              />
            ) : (
              <div className="center">
                {loading ? (
                  <Loading />
                ) : (
                  <>
                    {duplicates.length > 0 ? (
                      <h5>
                        {intl.formatMessage(
                          { defaultMessage: 'Select {modeFiles, select, yes {a file} other {an image}} from the list on the left.' },
                          {
                            modeFiles: mode === 'files' ? 'yes' : 'no',
                          },
                        )}
                      </h5>
                    ) : (
                      <h5>
                        {intl.formatMessage(
                          { defaultMessage: 'Nice work! You don\'t have any {similarMode, select, yes {similar} other {duplicate}} {filesMode, select, yes {files} other {images}} in your workspace.' },
                          {
                            similarMode: mode === 'similar_images' ? 'yes' : 'no',
                            filesMode: mode === 'files' ? 'yes' : 'no',
                          },
                        )}
                      </h5>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

AdminDuplicates.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

export default connect()(AdminDuplicates);
